import { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, X, Lock, Lightning, Star, Crown, ArrowRight, CreditCard, Receipt, Clock, ArrowClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const TIER_ORDER = ['free', 'starter', 'growth', 'pro', 'enterprise'];

const TIER_FEATURES = [
  { key: 'api_connections', label: 'API Connections', format: v => v === -1 ? 'Unlimited' : v },
  { key: 'jobs_per_month', label: 'Jobs / month', format: v => v === -1 ? 'Unlimited' : v.toLocaleString() },
  { key: 'nl_queries_per_month', label: 'NL Queries / month', format: v => v === -1 ? 'Unlimited' : v.toLocaleString() },
  { key: 'sub_constellations', label: 'Sub-Constellations', format: v => v === -1 ? 'Unlimited' : v === 0 ? '\u2014' : v },
  { key: 'heatcheck_retention', label: 'Heat Check Reports', format: v => v === -1 ? 'Unlimited' : v },
  { key: 'data_retention_days', label: 'Data Retention', format: v => v === -1 ? 'Unlimited' : v === 0 ? '\u2014' : v + ' days' },
  { key: 'commons_full', label: 'Full Commons Access', format: v => v },
  { key: 'signal_exchange', label: 'Signal Exchange', format: v => v },
  { key: 'signal_exchange_addon', label: 'Signal Add-on ($99/mo)', format: v => v },
  { key: 'matr_anomaly_feed', label: 'MATR Anomaly Feed', format: v => v },
  { key: 'matr_benchmarks', label: 'MATR Benchmarks', format: v => v },
  { key: 'matr_arc', label: 'MATR Arc', format: v => v },
  { key: 'matr_api', label: 'MATR API', format: v => v },
  { key: 'reporting_api', label: 'Reporting API', format: v => v },
];

const TIER_COLORS = {
  free: '#3D5A70', starter: '#0ABFA3', growth: '#7F77DD', pro: '#FFD700', enterprise: '#FF6B35',
};

const PACKAGE_MAP = { starter: 'starter', growth: 'growth', pro: 'pro' };

function BoolCell({ val }) {
  if (val === true) return <CheckCircle size={14} weight="fill" className="text-kova-green mx-auto" />;
  if (val === false) return <X size={12} className="text-kova-cyan/25 mx-auto" />;
  return <span className="text-[12px] font-mono text-kova-silv">{val}</span>;
}

export default function SubscriptionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [limits, setLimits] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [signalToggling, setSignalToggling] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null);

  const fetchData = useCallback(() => {
    Promise.all([
      fetch(`${API}/subscription/tiers`).then(r => r.json()),
      fetch(`${API}/subscription`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/billing/transactions`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
    ]).then(([t, s, txns]) => {
      setTiers(t);
      if (s) { setSubscription(s.subscription); setLimits(s.limits); }
      setTransactions(txns || []);
    }).catch(() => toast.error('Failed to load subscription data'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll payment status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 8;

    async function pollStatus() {
      if (attempts >= maxAttempts) {
        setPollingStatus(null);
        toast.error('Payment verification timed out. Check billing history.');
        setSearchParams({});
        return;
      }
      attempts++;
      setPollingStatus('checking');
      try {
        const res = await fetch(`${API}/billing/checkout/status/${sessionId}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Poll failed');
        const data = await res.json();
        if (data.payment_status === 'paid') {
          setPollingStatus('paid');
          toast.success('Payment successful! Your plan has been upgraded.');
          setSearchParams({});
          fetchData();
          setTimeout(() => setPollingStatus(null), 3000);
          return;
        }
        if (data.status === 'expired') {
          setPollingStatus(null);
          toast.error('Payment session expired.');
          setSearchParams({});
          return;
        }
        setTimeout(pollStatus, 2000);
      } catch {
        setTimeout(pollStatus, 2000);
      }
    }

    pollStatus();
  }, [searchParams, setSearchParams, fetchData]);

  async function handleCheckout(packageId) {
    setCheckingOut(packageId);
    try {
      const originUrl = window.location.origin;
      const res = await fetch(`${API}/billing/checkout`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, origin_url: originUrl }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Checkout failed');
        setCheckingOut(null);
        return;
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch {
      toast.error('Checkout failed');
      setCheckingOut(null);
    }
  }

  async function handleSignalAddonCheckout() {
    setSignalToggling(true);
    try {
      const originUrl = window.location.origin;
      const res = await fetch(`${API}/billing/checkout`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: 'signal_addon', origin_url: originUrl }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Checkout failed');
        return;
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch {
      toast.error('Checkout failed');
    } finally {
      setSignalToggling(false);
    }
  }

  if (loading) return <div className="flex items-center justify-center h-64"><span className="font-mono text-kova-cyan animate-pulse">Loading subscription...</span></div>;

  const currentTier = subscription?.tier || 'free';
  const currentTierIdx = TIER_ORDER.indexOf(currentTier);
  const isFounding = currentTier === 'founding';

  return (
    <div className="space-y-6" data-testid="subscription-page">
      {/* Payment processing banner */}
      {pollingStatus && (
        <div className={`p-3 border ${pollingStatus === 'paid' ? 'bg-kova-green/10 border-kova-green/20' : 'bg-kova-cyan/10 border-kova-cyan/20'} flex items-center gap-3`}>
          {pollingStatus === 'checking' ? (
            <ArrowClockwise size={16} className="text-kova-cyan animate-spin" />
          ) : (
            <CheckCircle size={16} weight="fill" className="text-kova-green" />
          )}
          <span className="font-mono text-[11px] text-kova-white">
            {pollingStatus === 'checking' ? 'Verifying payment...' : 'Payment confirmed! Your plan has been upgraded.'}
          </span>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-lg">Subscription</h1>
        <p className="text-[10px] font-mono text-kova-slate mt-0.5">Manage your plan, features, and Intelligence Mesh access</p>
      </div>

      {/* Current Plan Card */}
      <div className="card-brutal p-5" data-testid="current-plan-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isFounding ? <Crown size={20} weight="fill" className="text-kova-gold" /> : <Star size={20} weight="fill" style={{ color: TIER_COLORS[currentTier] }} />}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold text-base" style={{ color: isFounding ? '#FFD700' : TIER_COLORS[currentTier] }}>
                  {isFounding ? 'Founding Constellation' : limits?.label || 'Free'}
                </span>
                {isFounding && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-gold/10 text-kova-gold border border-kova-gold/20 uppercase tracking-wider">Equity Partner</span>}
              </div>
              <p className="text-[11px] font-mono text-kova-slate mt-0.5">
                {limits?.api_connections === -1 ? 'Unlimited' : limits?.api_connections} connections
                {' · '}
                {limits?.jobs_per_month === -1 ? 'Unlimited' : limits?.jobs_per_month?.toLocaleString()} jobs/mo
                {' · '}
                {limits?.nl_queries_per_month === -1 ? 'Unlimited' : limits?.nl_queries_per_month?.toLocaleString()} queries/mo
              </p>
            </div>
          </div>
          {!isFounding && currentTier !== 'enterprise' && (
            <span className="text-[11px] font-mono text-kova-slate">{limits?.price_label}</span>
          )}
        </div>
        {/* Signal Exchange Add-on */}
        {(limits?.signal_exchange_addon && !limits?.signal_exchange) && !subscription?.signal_exchange_addon && (
          <div className="mt-4 pt-4 border-t border-kova-cyan/12 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Lightning size={14} weight="fill" className="text-kova-purple" />
                <span className="text-[12px] font-heading font-bold">Signal Exchange Add-on</span>
                <span className="text-[9px] font-mono text-kova-slate">$99/mo</span>
              </div>
              <p className="text-[10px] font-mono text-kova-slate mt-0.5 ml-5">Enable cross-constellation anonymized intelligence exchange</p>
            </div>
            <Button
              onClick={handleSignalAddonCheckout}
              disabled={signalToggling}
              size="sm"
              variant="outline"
              className="rounded-none font-mono text-[9px] uppercase tracking-wider border-kova-purple/30 text-kova-purple"
              data-testid="signal-exchange-checkout"
            >
              <CreditCard size={12} className="mr-1" />
              {signalToggling ? 'Processing...' : 'Purchase'}
            </Button>
          </div>
        )}
        {subscription?.signal_exchange_addon && (
          <div className="mt-4 pt-4 border-t border-kova-cyan/12 flex items-center gap-2">
            <Lightning size={14} weight="fill" className="text-kova-purple" />
            <span className="text-[12px] font-heading font-bold">Signal Exchange</span>
            <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-green/10 text-kova-green border border-kova-green/20 uppercase tracking-wider">Active</span>
          </div>
        )}
      </div>

      {/* Tier Comparison Table */}
      <div className="card-brutal overflow-hidden" data-testid="tier-comparison">
        <div className="px-5 py-3 border-b border-kova-cyan/12">
          <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Plan Comparison</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-kova-cyan/12">
                <th className="text-left px-4 py-3 text-[10px] font-mono text-kova-slate uppercase tracking-wider w-[200px]">Feature</th>
                {tiers.map(t => (
                  <th key={t.tier_key} className="text-center px-3 py-3 min-w-[120px]">
                    <div className="text-[12px] font-heading font-bold" style={{ color: TIER_COLORS[t.tier_key] }}>{t.label}</div>
                    <div className="text-[10px] font-mono text-kova-slate">{t.price_label}</div>
                    {t.tier_key === currentTier && <div className="text-[7px] font-mono text-kova-cyan uppercase tracking-wider mt-1">Current</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TIER_FEATURES.map(feat => (
                <tr key={feat.key} className="border-b border-kova-cyan/6 hover:bg-kova-bg">
                  <td className="px-4 py-2.5 text-[11px] font-mono text-kova-silv">{feat.label}</td>
                  {tiers.map(t => {
                    const val = feat.format(t[feat.key]);
                    return (
                      <td key={t.tier_key} className="text-center px-3 py-2.5">
                        {typeof val === 'boolean' ? <BoolCell val={val} /> : <span className="text-[11px] font-mono text-kova-silv">{val}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Action row */}
              <tr>
                <td className="px-4 py-3" />
                {tiers.map(t => {
                  const tierIdx = TIER_ORDER.indexOf(t.tier_key);
                  const isCurrent = t.tier_key === currentTier;
                  const isDowngrade = tierIdx < currentTierIdx;
                  const isEnterprise = t.tier_key === 'enterprise';
                  const isFree = t.tier_key === 'free';
                  const packageId = PACKAGE_MAP[t.tier_key];
                  return (
                    <td key={t.tier_key} className="text-center px-3 py-3">
                      {isCurrent ? (
                        <span className="text-[9px] font-mono text-kova-cyan uppercase tracking-wider">Current Plan</span>
                      ) : isEnterprise ? (
                        <Button size="sm" variant="outline" className="rounded-none font-mono text-[9px] uppercase tracking-wider border-[#FF6B35]/30 text-[#FF6B35]" data-testid="contact-sales-btn">
                          Contact Sales
                        </Button>
                      ) : isFree || isDowngrade ? (
                        <span className="text-[9px] font-mono text-kova-slate">{isFree ? '' : 'Downgrade'}</span>
                      ) : packageId ? (
                        <Button
                          onClick={() => handleCheckout(packageId)}
                          disabled={!!checkingOut || isFounding}
                          size="sm"
                          variant="outline"
                          className="rounded-none font-mono text-[9px] uppercase tracking-wider border-kova-cyan/30 text-kova-cyan"
                          data-testid={`checkout-${t.tier_key}-btn`}
                        >
                          {checkingOut === packageId ? (
                            <ArrowClockwise size={12} className="animate-spin mr-1" />
                          ) : (
                            <CreditCard size={12} className="mr-1" />
                          )}
                          {checkingOut === packageId ? 'Processing...' : 'Upgrade'}
                        </Button>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Billing History */}
      <div className="card-brutal overflow-hidden" data-testid="billing-history">
        <div className="px-5 py-3 border-b border-kova-cyan/12 flex items-center gap-2">
          <Receipt size={14} className="text-kova-slate" />
          <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Billing History</span>
        </div>
        {transactions.length === 0 ? (
          <div className="p-8 text-center">
            <CreditCard size={24} className="mx-auto mb-2 text-kova-slate" />
            <p className="text-kova-silv text-xs">No transactions yet</p>
          </div>
        ) : (
          <div>
            {transactions.map(txn => {
              const isPaid = txn.payment_status === 'paid';
              const isPending = txn.payment_status === 'initiated' || txn.payment_status === 'unpaid';
              return (
                <div key={txn.transaction_id || txn.session_id} className="px-5 py-3 border-b border-kova-cyan/6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isPaid ? (
                      <CheckCircle size={14} weight="fill" className="text-kova-green" />
                    ) : isPending ? (
                      <Clock size={14} className="text-kova-amber" />
                    ) : (
                      <X size={14} className="text-kova-red" />
                    )}
                    <div>
                      <span className="font-mono text-[11px] text-kova-white">{txn.package_name}</span>
                      <span className="font-mono text-[9px] text-kova-slate ml-2">
                        {new Date(txn.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[11px] text-kova-silv">${txn.amount?.toFixed(2)}</span>
                    <span className={`font-mono text-[9px] uppercase tracking-wider ${isPaid ? 'text-kova-green' : isPending ? 'text-kova-amber' : 'text-kova-red'}`}>
                      {txn.payment_status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
